import React, { useEffect, useState, useCallback } from "react";
import api, { API, apiError, inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RazorpayButton } from "@/components/RazorpayButton";
import { Landmark, Upload, Clock, LogOut, UserCheck, Gift } from "lucide-react";

export const FranchisePending = ({ onLogout }) => {
  const [info, setInfo] = useState(null);
  const [pay, setPay] = useState({});
  const [amount, setAmount] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/member/franchise");
    setInfo(data); setAmount((a) => a || String(data.price));
  }, []);
  useEffect(() => {
    load();
    fetch(`${API}/public/payment-info`).then((r) => r.json()).then(setPay).catch(() => {});
  }, [load]);

  const submit = async () => {
    if (!file) { toast.error("Please attach your payment receipt"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("amount", amount); fd.append("receipt", file);
      await api.post("/member/franchise/pay", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt submitted — awaiting company approval"); setFile(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sub = info?.submission;
  return (
    <div className="p-4 sm:p-8 max-w-2xl mx-auto space-y-5" data-testid="franchise-pending">
      <Card className="p-6 bg-card/40 border-gold/30">
        <p className="eyebrow text-gold mb-1">Franchise membership</p>
        <h1 className="font-serif text-2xl text-white font-semibold">Complete your joining</h1>
        <p className="text-sm text-muted-foreground mt-2">Pay the franchise fee of <span className="text-gold font-mono">{inr(info?.price)}</span> and upload the receipt. Your account unlocks once the company approves it.</p>
        {sub && (
          <div className="mt-4 flex items-center gap-2 text-sm" data-testid="franchise-submission-status">
            <Clock className="w-4 h-4 text-amber-300" /> Receipt of {inr(sub.amount)} submitted
            <Badge variant="outline" className="border-amber-400/40 text-amber-300 capitalize">{sub.status}</Badge>
          </div>
        )}
      </Card>
      <Card className="p-6 bg-card/40 border-gold/30" data-testid="franchise-payment-info">
        <p className="font-serif text-white font-semibold flex items-center gap-2 mb-3"><Landmark className="w-4 h-4 text-gold" /> How to pay</p>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div className="space-y-1 text-muted-foreground">
            {pay.account_name && <p>Name: <span className="text-white">{pay.account_name}</span></p>}
            {pay.bank_name && <p>Bank: <span className="text-white">{pay.bank_name}</span></p>}
            {pay.account_number && <p>A/C: <span className="text-white font-mono">{pay.account_number}</span></p>}
            {pay.ifsc && <p>IFSC: <span className="text-white font-mono">{pay.ifsc}</span></p>}
            {pay.upi_id && <p>UPI: <span className="text-gold font-mono">{pay.upi_id}</span></p>}
          </div>
          {pay.qr_path && <img src={`${API}/public/payment-info/qr`} alt="Payment QR" className="w-36 h-36 rounded-xl border border-gold/40 object-cover" />}
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs text-muted-foreground">Amount paid (₹)</Label><Input data-testid="franchise-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 bg-card/60" /></div>
          <label className="block"><span className="text-xs text-muted-foreground">Payment receipt</span>
            <span className="mt-1 flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-gold/50 text-gold text-xs cursor-pointer hover:bg-gold/10"><Upload className="w-3.5 h-3.5" /> {file ? file.name : "Choose screenshot / PDF"}</span>
            <input data-testid="franchise-receipt-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <Button data-testid="franchise-submit-btn" onClick={submit} disabled={busy} className="mt-4 bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{busy ? "Submitting…" : "Submit Receipt"}</Button>
        <RazorpayButton token={localStorage.getItem("token")} body={{ kind: "franchise" }} label={`Pay ${inr(info?.price)} online (UPI / card)`} testid="franchise-razorpay-btn" className="mt-4 ml-2" onPaid={() => window.location.reload()} />
      </Card>
      <Button variant="outline" data-testid="franchise-logout-btn" onClick={onLogout} className="border-gold/40 text-gold gap-2"><LogOut className="w-4 h-4" /> Log out</Button>
    </div>
  );
};

export const PendingMembers = () => {
  const [list, setList] = useState([]);
  const token = localStorage.getItem("token");
  const load = useCallback(async () => setList((await api.get("/members/pending")).data), []);
  useEffect(() => { load(); }, [load]);
  const approve = async (id, mode) => {
    try {
      const { data } = await api.post(`/members/${id}/approve`, { mode });
      toast.success(mode === "free" ? "Approved free of charge" : data.commission_distributed ? "Approved — franchise commission distributed" : "Approved as paid");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  if (!list.length) return null;
  return (
    <Card className="p-5 bg-card/40 border-amber-400/30" data-testid="pending-members-card">
      <p className="font-serif font-semibold text-white flex items-center gap-2 mb-3"><UserCheck className="w-4 h-4 text-gold" /> New members awaiting approval ({list.length})</p>
      {list.map((m) => (
        <div key={m.id} data-testid={`pending-member-${m.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2.5 border-t border-border/40">
          <div className="min-w-0">
            <p className="text-sm text-white">{m.name} <span className="text-xs text-muted-foreground font-mono">· {m.phone}</span></p>
            <p className="text-[11px] text-muted-foreground">{m.referred_by_name ? `Joined under ${m.referred_by_name}` : "Direct joining"} · {m.submission ? `Receipt ${inr(m.submission.amount)}` : "No payment yet"}</p>
          </div>
          <div className="flex items-center gap-2">
            {m.submission && <a href={`${API}/member/franchise/${m.submission.id}/receipt?auth=${token}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-gold underline">View receipt</a>}
            <Button size="sm" data-testid={`approve-paid-${m.id}`} onClick={() => approve(m.id, "paid")} className="bg-gold text-[#0a2e22] hover:bg-gold/90 text-[10px] h-7">Approve (paid)</Button>
            <Button size="sm" variant="outline" data-testid={`approve-free-${m.id}`} onClick={() => approve(m.id, "free")} className="border-emerald-400/40 text-emerald-300 text-[10px] h-7 gap-1"><Gift className="w-3 h-3" /> Approve free</Button>
          </div>
        </div>
      ))}
    </Card>
  );
};
